import { forwardRef, type ReactNode, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { FormField, useFieldIds } from "../FormField/FormField";

export interface SelectOption {
  /** Lo que viaja en el formulario. */
  value: string;
  /** Lo que se lee en la lista. */
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size" | "children"> {
  label?: ReactNode;
  labelAction?: ReactNode;
  hint?: ReactNode;
  /** Su presencia pone el campo en rojo y enlaza `aria-invalid`/`aria-errormessage`. */
  error?: string;
  /** Las opciones. Se declaran como datos, no como JSX suelto. */
  options: readonly SelectOption[];
  /**
   * Primera opción, de valor `""`, para el estado "todavía no elegí".
   *
   * Existe porque un `<select>` sin ella NACE con la primera opción
   * seleccionada: alguien que nunca tocó el control acabaría registrado en
   * México sin haberlo dicho. Con el hueco vacío, no elegir es un estado
   * distinguible y el esquema puede exigir la elección.
   */
  placeholder?: string;
  /** Clases del contenedor del campo (no del `<select>`). */
  fieldClassName?: string;
}

/**
 * El desplegable del sistema. Un `<select>` NATIVO, a propósito.
 *
 * Un menú hecho a mano tendría que reimplementar —y mantener— la rueda del
 * teclado (flechas, inicio/fin, escribir para saltar, Escape), el rol y los
 * estados de ARIA, el desplazamiento del listado y el foco al cerrar. En
 * móvil, además, el nativo abre la rueda del sistema, que se maneja con el
 * pulgar y no se sale de la pantalla. Aquí no se pide nada que el nativo no
 * dé, así que reimplementarlo sería regalar accesibilidad a cambio de nada.
 *
 * Lo único que se le hace es vestirlo con el MISMO lenguaje visual que
 * `Input`: idéntico alto (`px-4 py-3 text-sm`), radio, borde, foco lila y
 * estados de error y deshabilitado. `appearance-none` quita la flecha del
 * sistema —que cambia de forma en cada navegador— y en su lugar va la del
 * sistema de diseño, decorativa y sin capturar el clic.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    labelAction,
    hint,
    error,
    options,
    placeholder,
    required,
    fieldClassName,
    className,
    id,
    disabled,
    "aria-describedby": extraDescribedBy,
    ...rest
  },
  ref,
) {
  const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
    hasHint: Boolean(hint),
    hasError: Boolean(error),
  });

  // Igual que en `Input`: se COMPONE con lo que venga de fuera en vez de
  // dejar que el spread lo pise y el campo pierda el enlace con su error.
  const describedByAll =
    [describedBy, extraDescribedBy].filter(Boolean).join(" ") || undefined;

  return (
    <FormField
      label={label}
      labelAction={labelAction}
      hint={hint}
      error={error}
      required={required}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
      className={fieldClassName}
    >
      <div className="relative">
        <select
          ref={ref}
          id={controlId}
          required={required}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={describedByAll}
          className={cn(
            "block w-full appearance-none rounded-2xl border bg-ds-surface px-4 py-3 pr-10 text-sm text-ds-ink",
            "outline-none transition",
            "focus:border-ds-lilac focus:ring-2 focus:ring-ds-lavender-bg-2/50",
            "disabled:cursor-not-allowed disabled:bg-ds-cream-2 disabled:opacity-60",
            error
              ? "border-ds-terracotta focus:border-ds-terracotta focus:ring-ds-terracotta/20"
              : "border-ds-border",
            className,
          )}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>

        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-3.5 my-auto h-4 w-4 text-ds-ink-3"
        />
      </div>
    </FormField>
  );
});
